import React, { useMemo } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import produce from 'immer';
import pluralize from 'pluralize';

import { createLoadMoreMethod } from '../../../../../../../../util/apollo';
import { AlbumForManualMatching } from '../../../../../../../../graphql/queries/albums';
import useSliceFilterOpts from '../../../../../../../../components/product/hooks/useSliceFilterOpts';
import {
  BulkVote,
  IdentityPubBoxCreate,
} from '../../../../../FaceTagPhoto/graphql/mutations';
import {
  albumPhotosDelete as albumPhotosDeleteMutation,
  albumPhotosHide as albumPhotosHideMutation,
  albumPhotosPublish as albumPhotosPublishMutation,
  albumPhotosMove as albumPhotosMoveMutation,
} from '../../../../../../../../graphql/mutations/albums';
import { ALBUM_MANUAL_MATCHING_FILTERS } from '../constants';
import { PHOTO_DOWNLOAD_MODAL } from '../../../../../../../../components/global/PhotoDownload';
import useOpenModal from '../../../../../../../../hooks/useOpenModal';
import useOpenAlert from '../../../../../../../../hooks/useOpenAlert';
import useRequestConfirm from '../../../../../../../../hooks/useRequestConfirm';
import { ALBUM_PHOTO_FILTERS } from '../../../../../../../../constants/albums';
import { VOTE_DECISION } from '../../../../../../../../constants/vote-decisions';

const useAlbumManualMatching = ({ albumId }) => {
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const requestConfirm = useRequestConfirm();
  const [bulkVote] = useMutation(BulkVote);
  const [identityPubBoxCreate] = useMutation(IdentityPubBoxCreate);
  const [albumPhotosDelete] = useMutation(albumPhotosDeleteMutation);
  const [albumPhotosHide] = useMutation(albumPhotosHideMutation);
  const [albumPhotosPublish] = useMutation(albumPhotosPublishMutation);
  const [albumPhotosMove] = useMutation(albumPhotosMoveMutation);
  const { filter, onFilterUpdate } = useSliceFilterOpts({
    initialFilter: {
      photosFilter: ALBUM_MANUAL_MATCHING_FILTERS.UNMATCHED,
      offset: 0,
      limit: 100,
    },
  });

  const {
    data: { album } = {},
    loading,
    refetch,
    fetchMore,
  } = useQuery(AlbumForManualMatching, {
    variables: {
      id: albumId,
      include_album_photos:
        filter.photosFilter === ALBUM_MANUAL_MATCHING_FILTERS.ALL,
      include_unmatched_photos:
        filter.photosFilter === ALBUM_MANUAL_MATCHING_FILTERS.UNMATCHED,
      offset: filter.offset,
      limit: filter.limit,
    },
    fetchPolicy: 'cache-and-network',
  });

  const photos = useMemo(() => {
    if (filter.photosFilter === ALBUM_MANUAL_MATCHING_FILTERS.UNMATCHED) {
      return album?.unmatched_photos?.records ?? [];
    }

    return album?.photos?.records ?? [];
  }, [album, filter]);

  const photosTotal = useMemo(() => {
    if (filter.photosFilter === ALBUM_MANUAL_MATCHING_FILTERS.UNMATCHED) {
      return album?.unmatched_photos?.total ?? 0;
    }

    return album?.photos?.total ?? 0;
  }, [album, filter]);

  const loadMore = useMemo(() => {
    let recordsPath = 'photos';
    if (filter.photosFilter === ALBUM_MANUAL_MATCHING_FILTERS.UNMATCHED) {
      recordsPath = 'unmatched_photos';
    }

    return createLoadMoreMethod({
      source: album,
      recordsPath,
      limit: filter.limit,
      fetchMore,
    });
  }, [album, filter]);

  const onBulkVote = async input => {
    const result = await bulkVote({
      variables: {
        input,
      },
      update: (store, { data: { bulkVote: bulkVoteResponse } }) => {
        if (
          bulkVoteResponse?.success &&
          filter.photosFilter === ALBUM_MANUAL_MATCHING_FILTERS.UNMATCHED
        ) {
          try {
            const data = store.readQuery({
              query: AlbumForManualMatching,
              variables: {
                id: albumId,
                include_album_photos: false,
                include_unmatched_photos: true,
                offset: filter.offset,
                limit: filter.limit,
              },
            });

            const votedPhotoIds = input.votes
              .filter(
                vote =>
                  !!vote.compared_photo_id &&
                  vote.decision === VOTE_DECISION.TOTALLY,
              )
              .map(vote => vote.compared_photo_id);

            const updatedData = produce(data, draft => {
              draft.album.unmatched_photos.records =
                draft.album.unmatched_photos.records.filter(
                  photo => !votedPhotoIds.includes(photo.id),
                );

              draft.album.unmatched_photos.total -= votedPhotoIds.length;
              if (draft.album.unmatched_photos.total < 0) {
                draft.album.unmatched_photos.total = 0;
              }
            });

            store.writeQuery({
              query: AlbumForManualMatching,
              variables: {
                id: albumId,
                include_album_photos: false,
                include_unmatched_photos: true,
                offset: filter.offset,
                limit: filter.limit,
              },
              data: updatedData,
            });
          } catch (e) {
            // Do nothing if the query isn't in the cache
          }
        }
      },
    });

    return result;
  };

  const onIdentityPubBoxCreate = async ({ identityId, photoId }) => {
    const defaultBox = [0.45, 0.45, 0.1, 0.1, 0];

    try {
      await identityPubBoxCreate({
        variables: {
          input: {
            identity_id: identityId,
            photo_id: photoId,
            box: defaultBox,
          },
        },
      });

      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  const onPhotosDownload = ({ downloadOriginalPhoto = false } = {}) => {
    openModal(PHOTO_DOWNLOAD_MODAL, {
      downloadOriginalPhoto,
    });
  };

  const onPhotosDelete = ({
    selectedPhotos,
    onResetSelection,
    onNavigateNextAfterRemoval,
  }) => {
    requestConfirm({
      actionSize: 'xs',
      message: (
        <div className="text-center">
          <h4>
            Delete {selectedPhotos.length > 1 ? 'these' : 'this'}{' '}
            {pluralize('photo', selectedPhotos.length)}?
          </h4>
          <p>This action cannot be undone.</p>
        </div>
      ),
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmBtnStyle: 'danger',
      onCancel: () => {},
      onConfirm: async () => {
        try {
          await albumPhotosDelete({
            variables: {
              input: {
                album: albumId,
                photo_ids: {
                  select_all: false,
                  selected_ids: selectedPhotos,
                  ignored_ids: [],
                },
                album_photo_filter: ALBUM_PHOTO_FILTERS.ALL,
                album_photo_group_ids: [],
                ungrouped_only: true,
                uploader_ids: [],
                album_photos_delete_mode: 'ALBUM_PHOTOS_ONLY',
              },
            },
            update: store => {
              try {
                const data = store.readQuery({
                  query: AlbumForManualMatching,
                  variables: {
                    id: albumId,
                    include_album_photos:
                      filter.photosFilter === ALBUM_MANUAL_MATCHING_FILTERS.ALL,
                    include_unmatched_photos:
                      filter.photosFilter ===
                      ALBUM_MANUAL_MATCHING_FILTERS.UNMATCHED,
                    offset: filter.offset,
                    limit: filter.limit,
                  },
                });

                const updatedData = produce(data, draft => {
                  if (draft?.album?.unmatched_photos?.records?.length > 0) {
                    draft.album.unmatched_photos.records =
                      draft.album.unmatched_photos.records.filter(
                        photo => !selectedPhotos.includes(photo.id),
                      );

                    draft.album.unmatched_photos.total -= selectedPhotos.length;
                    if (draft.album.unmatched_photos.total < 0) {
                      draft.album.unmatched_photos.total = 0;
                    }
                  }

                  if (draft?.album?.photos?.records?.length > 0) {
                    draft.album.photos.records =
                      draft.album.photos.records.filter(
                        photo => !selectedPhotos.includes(photo.id),
                      );

                    draft.album.photos.total -= selectedPhotos.length;
                    if (draft.album.photos.total < 0) {
                      draft.album.photos.total = 0;
                    }
                  }
                });

                store.writeQuery({
                  query: AlbumForManualMatching,
                  variables: {
                    id: albumId,
                    include_album_photos:
                      filter.photosFilter === ALBUM_MANUAL_MATCHING_FILTERS.ALL,
                    include_unmatched_photos:
                      filter.photosFilter ===
                      ALBUM_MANUAL_MATCHING_FILTERS.UNMATCHED,
                    offset: filter.offset,
                    limit: filter.limit,
                  },
                  data: updatedData,
                });
              } catch (e) {
                // Do nothing if the query isn't in the cache
              }
            },
          });

          onResetSelection();
          onNavigateNextAfterRemoval?.();
        } catch (error) {
          openAlert({
            message:
              'There was an error attempting to delete photos. Please try again later.',
            info: error.message,
          });
        }
      },
    });
  };

  const onPhotosHide = async ({ selectedPhotos, onResetSelection }) => {
    try {
      await albumPhotosHide({
        variables: {
          albumId,
          input: {
            album: albumId,
            photo_ids: {
              select_all: false,
              selected_ids: selectedPhotos,
              ignored_ids: [],
            },
            album_photo_filter: ALBUM_PHOTO_FILTERS.ALL,
            album_photo_group_ids: [],
            ungrouped_only: true,
            uploader_ids: [],
          },
        },
      });

      onResetSelection();
    } catch (error) {
      openAlert({
        message:
          'There was an error attempting to hide photos. Please try again later.',
        info: error.message,
      });
    }
  };

  const onPhotosPublish = async ({ selectedPhotos, onResetSelection }) => {
    try {
      await albumPhotosPublish({
        variables: {
          albumId,
          input: {
            album: albumId,
            photo_ids: {
              select_all: false,
              selected_ids: selectedPhotos,
              ignored_ids: [],
            },
            album_photo_filter: ALBUM_PHOTO_FILTERS.ALL,
            album_photo_group_ids: [],
            ungrouped_only: true,
            uploader_ids: [],
          },
        },
      });

      onResetSelection();
    } catch (error) {
      openAlert({
        message:
          'There was an error attempting to hide photos. Please try again later.',
        info: error.message,
      });
    }
  };

  const onPhotosMove = async ({
    selectedPhotos,
    onResetSelection,
    albumIds,
    photoGroupIds,
  }) => {
    try {
      await albumPhotosMove({
        input: {
          source_album_id: albumId,
          target_album_ids: albumIds,
          target_album_photo_group_ids: photoGroupIds,
          photo_ids: {
            select_all: false,
            selected_ids: selectedPhotos,
            ignored_ids: [],
          },
          album_photo_filter: ALBUM_PHOTO_FILTERS.ALL,
          ungrouped_only: true,
        },
      });

      onResetSelection();
    } catch (error) {
      openAlert({
        message:
          'There was an error attempting to move photos. Please try again later.',
        info: error.message,
      });
    }
  };

  return {
    album,
    loading,
    photos,
    photosTotal,
    refetch,
    filter,
    onFilterUpdate,
    loadMore,
    onBulkVote,
    onIdentityPubBoxCreate,
    onPhotosDownload,
    onPhotosDelete,
    onPhotosHide,
    onPhotosPublish,
    onPhotosMove,
  };
};

export default useAlbumManualMatching;
